import { getEmbeddedKey } from './embeddedKey'

import type {
  TCertificateShareMethod,
  TCertificateShareSurface,
} from '@repo/types'

/**
 * VRG-778 — THE one place a link leaves this app.
 *
 * ## Why this exists (working-conventions §36)
 *
 * The "open the OS share sheet, fall back to the clipboard" pattern was
 * hand-rolled in `SharePage` (`shareView`), and the bare clipboard copy was
 * hand-rolled twice more — `SharePage.copyLink` and
 * `ViewSettingsDrawer.copyLink`, with a third variant in
 * `RecoveryCodesStep`. The public certificate's share affordance would have been
 * the next copy, so §36's second-hand-roll rule applies: one helper, and the
 * existing sites move onto it rather than a fourth being added beside them.
 *
 * ⚠️ One correction to the ticket worth recording, because it changes what
 * "three places" meant: `CertificatesMenu.tsx:158` is NOT a third
 * implementation. It is a COMMENT describing this behaviour and pointing at
 * `SharePage`; its `Share` row navigates to the Share screen. Grepped and
 * confirmed — `navigator.share` appears in exactly one executable place in
 * `apps/app` today.
 *
 * ## THE TRAP: the share call must stay inside the user's gesture
 *
 * `navigator.share()` and `navigator.clipboard.writeText()` both require
 * transient user activation. On iOS Safari an `await` of anything before them
 * loses that activation and the call rejects with `NotAllowedError` — so this
 * helper must NEVER await a network request, a token mint, or anything else
 * before invoking them.
 *
 * That is why {@link shareLink} is synchronous up to the share call, why the
 * share token is minted locally (see `isValidShareToken` in `@repo/types`), and
 * why the analytics POST is handed to `onShared` to fire in PARALLEL rather than
 * being awaited first. Reordering those is the one change here that will look
 * harmless, pass every automated check, and break sharing on iPhone only.
 */

/** Which surface this page is: see `TCertificateShareSurface`. */
export function currentShareSurface(): TCertificateShareSurface {
  // `getEmbeddedKey()` reads the installation snippet's `data-embedded-key`
  // script attribute, which exists only when a brand's storefront embedded the
  // app. It is the same signal the rest of the app uses to tell the two apart.
  return getEmbeddedKey() ? 'embed' : 'hosted'
}

/** Does this platform have an OS share sheet? */
export function canUseNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

/** 16 lowercase hex chars — see `isValidShareToken` in `@repo/types`. */
export function mintShareToken(): string {
  const bytes = new Uint8Array(8)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
  } else {
    // A webview old enough to lack `crypto.getRandomValues` is vanishingly rare,
    // but the token must still be WELL-FORMED or the share event is refused for
    // its shape and the share silently stops being recorded. `Math.random` is
    // not cryptographically random and does not need to be: the token's only job
    // is correlation, and it grants nothing (see @repo/types).
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

export interface ShareLinkOptions {
  /** The absolute URL to send. */
  url: string
  /** Optional title/text for the native sheet. */
  title?: string
  text?: string
  /**
   * What to call the thing being shared, for the clipboard confirmation —
   * e.g. `'Certificate link'` produces "Certificate link copied to clipboard".
   */
  label?: string
  /** Shows the clipboard confirmation. Nothing is shown on the native path. */
  onCopied?: (message: string) => void
  /**
   * Fired AFTER the sheet opened or the clipboard was written, with how it went
   * out. This is where analytics belongs — never before the share call, which
   * would cost the gesture (see this file's docblock).
   */
  onShared?: (method: TCertificateShareMethod) => void
  /**
   * A genuine failure: no share sheet AND the clipboard refused (an insecure
   * origin, a denied permission, a webview with neither API). A user dismissing
   * the native sheet is NOT a failure and does not call this.
   */
  onError?: (error: unknown) => void
}

/**
 * Send a link through the OS share sheet, falling back to the clipboard.
 *
 * Call this DIRECTLY from the click handler. Do not await anything first.
 *
 * Returns which path it took, or `null` if neither was available.
 */
export async function shareLink(
  options: ShareLinkOptions,
): Promise<TCertificateShareMethod | null> {
  const { url, title, text, label, onCopied, onShared, onError } = options

  if (canUseNativeShare()) {
    try {
      // NOT awaited before `onShared` on purpose: the sheet is open the moment
      // this resolves... but it resolves on DISMISSAL, and the Web Share API
      // does not report whether anything was sent. So the share is recorded as
      // "the sheet opened", which is the honest claim and exactly why the `?s=`
      // token and its open counter exist — see @repo/types.
      await navigator.share({ url, ...(title ? { title } : {}), ...(text ? { text } : {}) })
      onShared?.('native')
      return 'native'
    } catch (error) {
      // `AbortError` is the user backing out of the sheet. That is an ordinary
      // outcome, not an error worth surfacing — and it must NOT fall through to
      // the clipboard, which would silently overwrite what they had copied
      // after they chose not to share.
      if ((error as { name?: string } | null)?.name === 'AbortError') return null

      // Anything else means the sheet did not work (a webview that advertises
      // `navigator.share` and rejects, a non-secure context). Fall through and
      // copy, so the user still gets the link.
    }
  }

  try {
    await navigator.clipboard.writeText(url)
    onCopied?.(`${label ?? 'Link'} copied to clipboard`)
    onShared?.('clipboard')
    return 'clipboard'
  } catch (error) {
    onError?.(error)
    return null
  }
}

/**
 * Copy a link, with no share sheet.
 *
 * Kept as its own function rather than a flag on {@link shareLink}, because a
 * `Copy` control must NEVER escalate into a share sheet — on a phone that would
 * mean pressing "Copy link" and being shown a send-to dialog, which is a
 * different action from the one the label promised.
 */
export async function copyLink(options: {
  url: string
  label?: string
  onCopied?: (message: string) => void
  onError?: (error: unknown) => void
}): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(options.url)
    options.onCopied?.(`${options.label ?? 'Link'} copied to clipboard`)
    return true
  } catch (error) {
    options.onError?.(error)
    return false
  }
}

/**
 * Append a share token to a URL as `?s=<token>`.
 *
 * `URL` rather than string concatenation so an address that already carries a
 * query (every `?view=`-bearing destination does) does not end up with two `?`.
 * Falls back to the original URL if it will not parse — a share with no token
 * still works, it just cannot be credited with its opens.
 */
export function withShareToken(url: string, token: string): string {
  try {
    const parsed = new URL(url)
    parsed.searchParams.set('s', token)
    return parsed.toString()
  } catch {
    return url
  }
}
